import '../../assets/fonts.css';

import cn from 'classnames';
import React, { forwardRef, type ReactElement, type Ref } from 'react';

import styles from './Text.css';

export type TextTag =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'label'
  | 'strong'
  | 'em'
  | 'small'
  | 'legend'
  | 'figcaption'
  | 'blockquote'
  | 'time'
  | 'a';

export type TextVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'h1Numeric'
  | 'h2Numeric'
  | 'h3Numeric'
  | 'body1Tight'
  | 'body1TightBold'
  | 'body1Loose'
  | 'body1LooseBold'
  | 'body2Tight'
  | 'body2TightBold'
  | 'body2Loose'
  | 'body2LooseBold'
  | 'caption'
  | 'captionBold'
  | 'xl'
  | 'numbers'
  | 'capsL'
  | 'capsM'
  | 'capsS'
  | 'microtext';

export type TextProps<T extends TextTag> = {
  as: T;
  variant: TextVariant;
  className?: string;
  ref?: Ref<HTMLElementTagNameMap[T]>;
} & Omit<JSX.IntrinsicElements[T], 'className' | 'ref'>;

const variantClassName: Record<TextVariant, string> = {
  h1: styles.Text_variant_h1,
  h2: styles.Text_variant_h2,
  h3: styles.Text_variant_h3,
  h4: styles.Text_variant_h4,
  h5: styles.Text_variant_h5,
  h6: styles.Text_variant_h6,
  h1Numeric: styles.Text_variant_h1Numeric,
  h2Numeric: styles.Text_variant_h2Numeric,
  h3Numeric: styles.Text_variant_h3Numeric,
  body1Tight: styles.Text_variant_body1Tight,
  body1TightBold: styles.Text_variant_body1TightBold,
  body1Loose: styles.Text_variant_body1Loose,
  body1LooseBold: styles.Text_variant_body1LooseBold,
  body2Tight: styles.Text_variant_body2Tight,
  body2TightBold: styles.Text_variant_body2TightBold,
  body2Loose: styles.Text_variant_body2Loose,
  body2LooseBold: styles.Text_variant_body2LooseBold,
  caption: styles.Text_variant_caption,
  captionBold: styles.Text_variant_captionBold,
  xl: styles.Text_variant_xl,
  numbers: styles.Text_variant_numbers,
  capsL: styles.Text_variant_capsL,
  capsM: styles.Text_variant_capsM,
  capsS: styles.Text_variant_capsS,
  microtext: styles.Text_variant_microtext,
};

type TextComponent = <T extends TextTag>(
  props: TextProps<T>
) => ReactElement | null;

const TextWithRef = <T extends TextTag>(
  { as: Component, variant, className, ...props }: TextProps<T>,
  ref: Ref<HTMLElementTagNameMap[T]>
) =>
  React.createElement(Component, {
    ...props,
    ref,
    className: cn(styles.Text, variantClassName[variant], className),
  });

export const Text = forwardRef(TextWithRef) as TextComponent;
